import {
  Box,
  Heading,
  useToast,
  FormLabel,
  Input,
  VStack,
  Text,
  SimpleGrid,
  Container,
  Button,
  Collapse,
  useDisclosure,
} from "@chakra-ui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import { IField } from "../../types/fieldInterface";

import useAxios from "../../utils/useAxios";
import { MoistureIndexLegend } from "../../views/deviceMapColorUtils";
import { getMoistureIndexThresholds } from "../../utils/colorUtils/mapColorUtils";

export default function MoistureLimitsTab({
  field,
  setField,
  isAutomaticMode = false,
}: {
  field: IField;
  setField: React.Dispatch<React.SetStateAction<IField>>;
  isAutomaticMode?: boolean;
}) {
  const toast = useToast();
  const { isOpen, onToggle } = useDisclosure();
  const api = useAxios();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: keyof IField) => {
    const value = parseFloat(e.target.value);
    setField((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const getHeightPercentage = (value: number) => {
    return `${value}%`;
  };

  const getSuggested = () => {
    console.log(field);
    if (
      !field?.soil_texture_sand ||
      !field?.soil_texture_clay ||
      !field?.soil_texture_silt ||
      !field?.soil_texture_organic_matter ||
      !field?.crop_type
    ) {
      toast({
        title: <Trans>Fill in soil texture and crop type first</Trans>,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    api
      .post(`agtech/get-moisture-limits/`, {
        soil_texture_sand: field.soil_texture_sand,
        soil_texture_clay: field.soil_texture_clay,
        soil_texture_silt: field.soil_texture_silt,
        soil_texture_organic_matter: field.soil_texture_organic_matter,
        crop_type: field.crop_type,
      })
      .then((response) => {
        setField((prev) => (prev ? { ...prev, ...response.data } : prev));
        toast({
          title: <Trans>Suggested limits set</Trans>,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          title: <Trans>Error setting suggested limits</Trans>,
          status: "error",
          description: error.response.data,
          duration: 9000,
          isClosable: true,
        });
      });
  };

  const moistureIndexThresholds = getMoistureIndexThresholds(field);

  if (isAutomaticMode) {
    return (
      <Container maxW="container.xl">
        <Heading size="lg" mb={4}>
          <Trans>Moisture Limits</Trans>
        </Heading>
        <Text mb={4}>
          <Trans>
            Automatic mode uses moisture index limits based on crop type. No manual soil or moisture
            limit configuration is required.
          </Trans>
        </Text>
        {!field.crop_type ? (
          <Text mb={4}>
            <Trans>Select a crop type to preview the automatic moisture limits.</Trans>
          </Text>
        ) : moistureIndexThresholds.management === undefined ? (
          <Text mb={4}>
            <Trans>Selected crop type does not have moisture index thresholds configured.</Trans>
          </Text>
        ) : (
          <MoistureIndexLegend thresholds={moistureIndexThresholds} />
        )}
      </Container>
    );
  }

  return (
    <Container maxW="container.xl">
      <Heading size="lg" mb={4}>
        <Trans>Moisture Limits</Trans>
      </Heading>
      <Text mb={4}>
        <Trans>
          Set the moisture limits for the field. "Get suggested" estimates limits based on soil
          texture and crop type.
        </Trans>
      </Text>
      <Button onClick={onToggle} mb={4}>
        {isOpen ? <Trans>Hide Explanation</Trans> : <Trans>View Explanation</Trans>}
      </Button>
      <Button mb={4} ml={2} colorScheme="teal" onClick={getSuggested}>
        <Trans>Get Suggested</Trans>
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Box p={4} mb={4} borderWidth="1px" borderRadius="lg">
          <Text mb={2}>
            <strong>
              <Trans>Permanent Wilting Point (%)</Trans>:
            </strong>
          </Text>
          <Text mb={4}>
            <Trans>
              This is the moisture level at which plants wilt and do not recover even if water is
              added. It represents the lower limit of available water for plants.
            </Trans>
          </Text>
          <Text mb={2}>
            <strong>
              <Trans>Moisture Limit Damage (%)</Trans>:
            </strong>
          </Text>
          <Text mb={4}>
            <Trans>
              This is the moisture level at which plants start to experience damage due to
              insufficient water. It is a critical threshold that should be avoided to prevent harm
              to the plants.
            </Trans>
          </Text>
          <Text mb={2}>
            <strong>
              <Trans>Moisture Limit Deficit (%)</Trans>:
            </strong>
          </Text>
          <Text mb={4}>
            <Trans>
              This represents the deficit in moisture limit, indicating how much the current
              moisture level is below the optimal or required level. It helps in understanding the
              water needs of the field.
            </Trans>
          </Text>
          <Text mb={2}>
            <strong>
              <Trans>Field Capacity (%)</Trans>:
            </strong>
          </Text>
          <Text mb={4}>
            <Trans>
              This is the maximum amount of moisture the field can hold after excess water has
              drained away. It represents the upper limit of available water for plants.
            </Trans>
          </Text>
        </Box>
      </Collapse>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
        <Box>
          <VStack spacing={4} align="stretch">
            <Box>
              <FormLabel>
                <Trans>Permanent Wilting Point (%)</Trans>
              </FormLabel>
              <Input
                type="number"
                placeholder={t`Permanent Wilting Point`}
                value={field?.moisture_limit_critical || ""}
                onChange={(e) => handleInputChange(e, "moisture_limit_critical")}
              />
            </Box>
            <Box>
              <FormLabel>
                <Trans>Moisture Limit Damage (%)</Trans>
              </FormLabel>
              <Input
                type="number"
                placeholder={t`Moisture Limit Damage`}
                value={field?.moisture_limit_damage || ""}
                onChange={(e) => handleInputChange(e, "moisture_limit_damage")}
              />
            </Box>
            <Box>
              <FormLabel>
                <Trans>Moisture Limit Deficit (%)</Trans>
              </FormLabel>
              <Input
                type="number"
                placeholder={t`Moisture Limit Deficit`}
                value={field?.moisture_limit_deficit || ""}
                onChange={(e) => handleInputChange(e, "moisture_limit_deficit")}
              />
            </Box>
            <Box>
              <FormLabel>
                <Trans>Field Capacity (%)</Trans>
              </FormLabel>
              <Input
                type="number"
                placeholder={t`Field Capacity`}
                value={field?.moisture_limit_field_capacity || ""}
                onChange={(e) => handleInputChange(e, "moisture_limit_field_capacity")}
              />
            </Box>
          </VStack>
        </Box>
        <Box>
          <Heading size="md" mb={4}>
            <Trans>Visualization</Trans>
          </Heading>
          {field?.moisture_limit_critical &&
          field?.moisture_limit_damage &&
          field?.moisture_limit_deficit &&
          field?.moisture_limit_field_capacity ? (
            <Box height="300" ml="5" width="150px" position="relative">
              <Box
                position="absolute"
                bottom="0"
                height={getHeightPercentage(field?.moisture_limit_critical)}
                width="50px"
                bg="black"
              />
              <Text
                position="absolute"
                left={"50px"}
                bottom={`calc(${getHeightPercentage(field?.moisture_limit_critical)} - 10px)`}
                width="100%"
                textAlign="left"
                fontSize="sm"
              >
                <Trans>{`--  ${field?.moisture_limit_critical} % - PWP`}</Trans>
              </Text>
              <Box
                position="absolute"
                bottom={getHeightPercentage(field?.moisture_limit_critical)}
                height={getHeightPercentage(field.moisture_limit_damage)}
                width="50px"
                bg="red"
              />
              <Text
                position="absolute"
                left={"50px"}
                bottom={`calc(${getHeightPercentage(field?.moisture_limit_damage)} - 10px)`}
                width="100%"
                textAlign="left"
                fontSize="sm"
              >
                <Trans>{`--  ${field?.moisture_limit_damage} % - Damage`}</Trans>
              </Text>
              <Box
                position="absolute"
                bottom={getHeightPercentage(field?.moisture_limit_damage)}
                height={getHeightPercentage(field.moisture_limit_deficit)}
                width="50px"
                bg="orange"
              />
              <Text
                position="absolute"
                left={"50px"}
                bottom={`calc(${getHeightPercentage(field?.moisture_limit_deficit)} - 10px)`}
                width="100%"
                textAlign="left"
                fontSize="sm"
              >
                <Trans>{`--  ${field?.moisture_limit_deficit} % - Deficit`}</Trans>
              </Text>
              <Box
                position="absolute"
                bottom={getHeightPercentage(field?.moisture_limit_deficit)}
                height={getHeightPercentage(field.moisture_limit_field_capacity)}
                width="50px"
                bg="green.400"
              />
              <Text
                position="absolute"
                left={"50px"}
                bottom={`calc(${getHeightPercentage(field?.moisture_limit_field_capacity)} - 10px)`}
                width="100%"
                textAlign="left"
                fontSize="sm"
              >
                <Trans>{`--  ${field?.moisture_limit_field_capacity} % - FC`}</Trans>
              </Text>
              <Box
                position="absolute"
                bottom={getHeightPercentage(field?.moisture_limit_field_capacity)}
                height={getHeightPercentage(100 - field.moisture_limit_field_capacity)}
                width="50px"
                bg="blue.600"
              />
            </Box>
          ) : (
            <Text mb={4}>
              <Trans>Please fill in all the fields to see the visualization</Trans>
            </Text>
          )}
        </Box>
      </SimpleGrid>
    </Container>
  );
}
